import firebase from 'firebase/app'
import { Instance, types } from 'mobx-state-tree'
import { SegmentKey } from 'shopify-payment-core'

import { appConfig } from '../config'
import { logAuth } from '../helpers/logger'
import { BaseModel } from '../models/BaseModel'

export const AuthModel = BaseModel.named('Auth')
  .props({
    token: types.maybe(types.string),
    uid: types.maybe(types.string),
    email: types.optional(types.string, ''),
    roles: types.optional(types.array(types.string), []),
    authEmail: types.maybe(types.string),
  })
  .views(self => ({
    get isAuthenticated() {
      return Boolean(self.token && self.uid)
    },
    get isAdmin() {
      return self.roles.includes('admin')
    },
    get accountRoles() {
      return self.roles.filter(role => role !== 'admin')
    },
    getSegmentAccess(segmentKey: SegmentKey = 'gopay') {
      return this.accountRoles.reduce(
        (access, role) => {
          const [segment, id] = role.split('@')
          if (segment === segmentKey) {
            return [...access, id]
          }
          return access
        },
        [] as string[],
      )
    },
  }))
  .actions(self => ({
    async login(email: string, origin: string = '') {
      self.authEmail = email
      await self.auth.sendSignInLinkToEmail(email, {
        url: `${appConfig.uri}/auth?o=${origin}`,
        handleCodeInApp: true,
      })
    },
    logout() {
      self.auth.signOut()
    },
    applyAuthentication(token: string, user: firebase.User) {
      self.token = token
      self.uid = user.uid
      self.email = user.email || ''
      self.authEmail = undefined
    },
    applyRoles(roles: string[]) {
      self.roles.replace(roles)
    },
    clearAuthentication() {
      self.token = undefined
      self.uid = undefined
      self.email = ''
      self.roles.clear()
    },
  }))
  .actions(self => {
    const watchRoles = (uid: ID) => {
      firebase
        .database()
        .ref(`/roles/${uid}`)
        .on('value', snapshot => {
          if (snapshot && snapshot.exists()) {
            const rolesMap = snapshot.val()
            const roles = Object.keys(rolesMap).filter(
              role => rolesMap[role] === true,
            )
            self.applyRoles(roles)
          }
        })
    }

    const authenticateUser = async (user: firebase.User) => {
      const token = await user.getIdToken()
      self.applyAuthentication(token, user)
      watchRoles(user.uid)
    }

    return {
      async handleAuthentication(email: string) {
        if (self.auth.isSignInWithEmailLink(window.location.href)) {
          const { user } = await self.auth.signInWithEmailLink(
            email,
            window.location.href,
          )
          if (user) {
            await authenticateUser(user)
            watchRoles(user.uid)
          }
        } else {
          logAuth('not valid auth in url')
        }
      },
      afterAttach() {
        if (self.auth.onIdTokenChanged) {
          self.auth.onIdTokenChanged(async user => {
            if (user) {
              await authenticateUser(user)
              watchRoles(user.uid)
            } else {
              self.clearAuthentication()
            }
          })
        }
      },
    }
  })

export interface TAuthModel extends Instance<typeof AuthModel> {}
